<template>
  <div class="briefs-list-pane">
    <SelectFolderModalToolbar
      :value="searchKey"
      placeholder="Search briefs..."
      @input="searchKey = $event"
      @clear="searchKey = ''"
    />

    <div
      v-if="isLoading"
      class="briefs-list-pane__state"
    >
      <b-spinner />
    </div>

    <div
      v-else-if="loadFailed"
      class="briefs-list-pane__state"
    >
      Could not load your briefs.
    </div>

    <div
      v-else-if="visibleBriefs.length === 0"
      class="briefs-list-pane__state"
    >
      {{ searchKey.trim() ? 'No briefs match your search.' : 'No briefs found.' }}
    </div>

    <div
      v-else
      class="briefs-list-pane__list"
    >
      <button
        v-for="brief in visibleBriefs"
        :key="brief.id"
        type="button"
        class="briefs-list-pane__row"
        :class="{
          'briefs-list-pane__row--static': brief.isPendingIntake,
          'briefs-list-pane__row--selected': selectable && String(selectedId) === String(brief.id),
        }"
        :title="brief.isPendingIntake ? 'Pending intake — not yet a brief you can browse' : null"
        @click="onRowClick(brief)"
      >
        <FileTextIcon
          :width="16"
          :height="16"
          class="briefs-list-pane__row-icon"
        />
        <span class="briefs-list-pane__row-body">
          <span class="briefs-list-pane__row-title">{{ brief.title }}</span>
          <span
            v-if="showOwnerSubtitle"
            class="briefs-list-pane__row-subtitle"
          >{{ brief.subtitle }}</span>
        </span>
        <span
          v-if="!showOwnerSubtitle"
          class="briefs-list-pane__row-updated"
        >Updated {{ brief.updatedAgo }}</span>
        <b-icon
          v-if="!brief.isPendingIntake"
          icon="chevron-right"
          class="briefs-list-pane__row-chevron"
        />
      </button>
    </div>
  </div>
</template>

<script>
import { FileText as FileTextIcon } from 'lucide-vue';
import { listBriefs } from '@frontend/components/briefs/briefsApi';
import { formatRelativeTime } from '@frontend/services/helpers/relative-time';
import SelectFolderModalToolbar from '@frontend/components/modules/file-browser/modals/SelectFolderModalToolbar.vue';

export default {
  name: 'BriefsListPane',

  components: {
    FileTextIcon,
    SelectFolderModalToolbar,
  },

  props: {
    // POR-13874: in "select a brief" mode the row highlights instead of the
    // caller navigating into the brief.
    selectable: {
      type: Boolean,
      default: false,
    },
    selectedId: {
      type: [String, Number],
      default: null,
    },
    // Swaps the "Updated <ago>" column for an owner + status subtitle under the
    // title, per the POR-13874 mockup. The File Browser keeps the old layout.
    showOwnerSubtitle: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      briefs: [],
      searchKey: '',
      isLoading: false,
      loadFailed: false,
    };
  },

  computed: {
    visibleBriefs() {
      const query = this.searchKey.trim().toLowerCase();

      if (!query) {
        return this.briefs;
      }

      return this.briefs.filter((brief) => brief.title.toLowerCase().includes(query));
    },
  },

  created() {
    this.fetchBriefs();
  },

  methods: {
    // "You" when the brief is the caller's own (`is_owner`, from the API),
    // "Team" otherwise — then the status. There is no "preview"/seeded-brief
    // concept in the API, so that branch from the mockup is intentionally
    // omitted rather than invented.
    //
    // Pending-intake rows carry no `is_owner` and a raw `status: 'pending_intake'`
    // token — the caller passes the same `isPendingIntake` signal the rest of
    // the component uses (chevron, click guard) so those rows show "Pending"
    // instead of leaking that token into the UI.
    ownerSubtitle(brief, isPendingIntake) {
      if (isPendingIntake) {
        return 'Pending';
      }

      const scope = brief.is_owner ? 'You' : 'Team';

      return `${scope} · ${String(brief.status || 'draft').toLowerCase()}`;
    },

    onRowClick(brief) {
      // Pending intakes aren't a Brief record yet — nothing to browse into (matches the
      // same guard BriefsIndexPage.vue uses before navigating).
      if (brief.isPendingIntake) {
        return;
      }
      this.$emit('select', { id: brief.id, title: brief.title });
    },

    async fetchBriefs() {
      this.isLoading = true;
      this.loadFailed = false;

      try {
        const res = await listBriefs({ per_page: 100 }, { internal: true });
        const rows = [
          ...((res && res.pending_intakes) || []),
          ...((res && res.data) || []),
        ];

        this.briefs = rows.map((b) => {
          const isPendingIntake = b.kind === 'pending_intake' || b.status === 'pending_intake';

          return {
            id: b.id,
            title: b.title || 'Untitled brief',
            updatedAgo: formatRelativeTime(b.updated_at) || 'just now',
            subtitle: this.ownerSubtitle(b, isPendingIntake),
            isPendingIntake,
          };
        });
      } catch (error) {
        console.error('Error fetching briefs:', error);
        this.loadFailed = true;
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.briefs-list-pane {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 360px;
}

.briefs-list-pane__state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: #73808c;
}

.briefs-list-pane__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: min(60vh, 560px);
  overflow-y: auto;
  overflow-x: hidden;
}

.briefs-list-pane__row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #e3e6e9;
  border-radius: 8px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.briefs-list-pane__row:hover {
  border-color: #c7ccd1;
  background-color: #f8f9fa;
}

.briefs-list-pane__row--static {
  cursor: default;
  color: #adb5bd;
}

.briefs-list-pane__row--static:hover {
  border-color: #e3e6e9;
  background-color: #fff;
}

.briefs-list-pane__row--selected {
  border-color: #379c75;
  background-color: #f1f9f6;
}

.briefs-list-pane__row-icon {
  flex: 0 0 auto;
  color: #8a929a;
}

.briefs-list-pane__row-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.briefs-list-pane__row-subtitle {
  color: #73808c;
  font-size: 12px;
}

.briefs-list-pane__row-title {
  flex: 1 1 auto;
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.briefs-list-pane__row-updated {
  flex: 0 0 auto;
  color: #73808c;
  font-size: 12px;
}

.briefs-list-pane__row-chevron {
  flex: 0 0 auto;
  color: #8a929a;
}
</style>
